import { useEffect, useState } from "preact/hooks";
import type { JSX } from "preact";
import { api, type BusinessHours, type DayKey } from "../../api";
import { DICTS, useI18n, type Lang } from "../../lib/i18n";
import { longDate } from "../../lib/format";
import { ReadOnlyNote, Section, SettingRow, SitePicker, StatusLine, Switch, useSelectedSite, useSites, useStatus } from "./common";

/**
 * When the team answers. Outside these hours the widget says so and collects
 * an e-mail address instead of promising a quick reply nobody will give.
 * An empty week means "always open" — a site that has not set hours behaves
 * like an ordinary chat.
 */

const DAY_KEYS: DayKey[] = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

const DEFAULT_RANGE: [string, string] = ["09:00", "18:00"];

const FALLBACK_ZONES = ["Europe/Istanbul", "Europe/London", "Europe/Berlin", "America/New_York", "Asia/Dubai", "UTC"];

function timeZones(): string[] {
  try {
    const list = (Intl as unknown as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf?.("timeZone");
    return list && list.length > 0 ? list : FALLBACK_ZONES;
  } catch {
    return FALLBACK_ZONES;
  }
}

export function rangeProblems(weekly: BusinessHours["weekly"], lang: Lang = "tr"): string | null {
  const t = DICTS[lang].hours;
  for (const [index, day] of DAY_KEYS.entries()) {
    const label = t.days[index]!;
    for (const [from, to] of weekly[day] ?? []) {
      if (!from || !to) return t.fillIn(label);
      if (from >= to) return t.order(label);
    }
  }
  return null;
}

const EMPTY: BusinessHours = {
  timezone: "Europe/Istanbul",
  weekly: {},
  offlineMessage: "We are offline right now. Leave us a message and we will get back to you as soon as possible.",
  offlineEmailTo: null,
  holidayDates: [],
};

export function HoursTab({ isAdmin }: { isAdmin: boolean }): JSX.Element {
  const { t: all, lang } = useI18n();
  const t = all.hours;
  const { sites } = useSites();
  const [site, selectSite] = useSelectedSite(sites);
  const [hours, setHours] = useState<BusinessHours | null>(null);
  const [saved, setSaved] = useState<string>("");
  const [holiday, setHoliday] = useState("");
  const [status, show, fail] = useStatus();

  useEffect(() => {
    if (!site) return;
    setHours(null);
    void api
      .businessHours(site.id)
      .then((res) => {
        const loaded = res.businessHours ? { ...EMPTY, ...res.businessHours } : EMPTY;
        setHours(loaded);
        setSaved(JSON.stringify(loaded));
      })
      .catch(fail);
  }, [site?.id, fail]);

  if (!site) return <div class="empty">{all.common.createSiteFirst}</div>;
  if (!hours) return <div class="empty">{all.common.loading}</div>;

  const alwaysOpen = DAY_KEYS.every((day) => (hours.weekly[day] ?? []).length === 0);
  const problem = rangeProblems(hours.weekly, lang);
  const dirty = JSON.stringify(hours) !== saved;
  const disabled = !isAdmin;

  const setDay = (day: DayKey, ranges: Array<[string, string]>): void =>
    setHours({ ...hours, weekly: { ...hours.weekly, [day]: ranges } });

  async function save(): Promise<void> {
    try {
      const res = await api.saveBusinessHours(site!.id, { ...hours!, offlineEmailTo: hours!.offlineEmailTo?.trim() || null });
      const next = { ...EMPTY, ...res.businessHours };
      setHours(next);
      setSaved(JSON.stringify(next));
      show("ok", t.saved);
    } catch (err) {
      fail(err);
    }
  }

  return (
    <>
      <ReadOnlyNote isAdmin={isAdmin} />
      <SitePicker sites={sites} value={site} onChange={selectSite} />

      <Section title={t.title}>
        <Switch
          label={t.alwaysOpen}
          hint={t.alwaysOpenHint}
          checked={alwaysOpen}
          disabled={disabled}
          onChange={(on) =>
            setHours({
              ...hours,
              weekly: on
                ? {}
                : Object.fromEntries(DAY_KEYS.map((day) => [day, day === "sat" || day === "sun" ? [] : [[...DEFAULT_RANGE] as [string, string]]])),
            })
          }
        />

        {alwaysOpen ? null : (
          <>
            <label>
              {t.timezone}
              <select value={hours.timezone} disabled={disabled} onChange={(e) => setHours({ ...hours, timezone: e.currentTarget.value })}>
                {timeZones().map((zone) => (
                  <option key={zone} value={zone}>
                    {zone}
                  </option>
                ))}
              </select>
            </label>

            <SettingRow title={t.weekTitle} hint={t.weekHint} wide>
              <div class="week">
              {DAY_KEYS.map((day, index) => {
                const label = t.days[index]!;
                const ranges = hours.weekly[day] ?? [];
                return (
                  <div key={day} class="day-row">
                    <label class="day-toggle">
                      <input
                        type="checkbox"
                        checked={ranges.length > 0}
                        disabled={disabled}
                        onChange={(e) => setDay(day, e.currentTarget.checked ? [[...DEFAULT_RANGE] as [string, string]] : [])}
                      />
                      {label}
                    </label>
                    <div class="ranges">
                      {ranges.length === 0 ? <span class="hint">{t.closed}</span> : null}
                      {ranges.map(([from, to], i) => (
                        <span key={i} class="range">
                          <input
                            type="time"
                            aria-label={t.from(label)}
                            value={from}
                            disabled={disabled}
                            onInput={(e) => setDay(day, ranges.map((r, j) => (j === i ? [e.currentTarget.value, r[1]] : r)))}
                          />
                          <span aria-hidden="true">–</span>
                          <input
                            type="time"
                            aria-label={t.to(label)}
                            value={to}
                            disabled={disabled}
                            onInput={(e) => setDay(day, ranges.map((r, j) => (j === i ? [r[0], e.currentTarget.value] : r)))}
                          />
                          {isAdmin ? (
                            <button class="btn ghost icon" aria-label={t.removeRange(label)} onClick={() => setDay(day, ranges.filter((_, j) => j !== i))}>
                              ×
                            </button>
                          ) : null}
                        </span>
                      ))}
                      {isAdmin && ranges.length > 0 && ranges.length < 4 ? (
                        <button class="btn sm ghost" onClick={() => setDay(day, [...ranges, [ranges[ranges.length - 1]![1], "23:59"] as [string, string]])}>
                          {t.addRange}
                        </button>
                      ) : null}
                    </div>
                  </div>
                );
              })}
              </div>
            </SettingRow>
            {problem ? <p class="field-error">{problem}</p> : null}
          </>
        )}
      </Section>

      <Section title={t.holidays} intro={t.holidaysIntro}>
        <SettingRow title={t.holidayList} wide>
          <div class="chips-row">
          {hours.holidayDates.length === 0 ? <span class="hint">{t.noHolidays}</span> : null}
          {hours.holidayDates.map((date) => (
            <span key={date} class="chip">
              {longDate(`${date}T12:00:00`, lang)}
              {isAdmin ? (
                <button aria-label={t.removeDate(date)} onClick={() => setHours({ ...hours, holidayDates: hours.holidayDates.filter((d) => d !== date) })}>
                  ×
                </button>
              ) : null}
            </span>
          ))}
          </div>
        </SettingRow>
        {isAdmin ? (
          <SettingRow title={t.addHoliday} hint={t.addHolidayHint} htmlFor="holiday-date">
          <form
            class="inline-actions"
            onSubmit={(e) => {
              e.preventDefault();
              if (!holiday || hours.holidayDates.includes(holiday)) return;
              setHours({ ...hours, holidayDates: [...hours.holidayDates, holiday].sort() });
              setHoliday("");
            }}
          >
            <input id="holiday-date" type="date" aria-label={t.date} value={holiday} onInput={(e) => setHoliday(e.currentTarget.value)} />
            <button class="btn" disabled={!holiday}>
              {all.common.add}
            </button>
          </form>
          </SettingRow>
        ) : null}
      </Section>

      <Section title={t.whenClosed}>
        <label>
          {t.offlineMessage}
          <textarea class="plain" maxLength={500} value={hours.offlineMessage} disabled={disabled} onInput={(e) => setHours({ ...hours, offlineMessage: e.currentTarget.value })} />
        </label>
        <label>
          {t.offlineEmail} <span class="hint">{all.common.optional}</span>
          <input
            type="email"
            value={hours.offlineEmailTo ?? ""}
            disabled={disabled}
            placeholder="support@example.com"
            onInput={(e) => setHours({ ...hours, offlineEmailTo: e.currentTarget.value })}
          />
        </label>
      </Section>

      {isAdmin ? (
        <div class="save-bar sticky">
          <button class="btn primary" disabled={!dirty || !!problem || !hours.offlineMessage.trim()} onClick={() => void save()}>
            {all.common.saveChanges}
          </button>
          {dirty ? (
            <button class="btn" onClick={() => setHours(JSON.parse(saved) as BusinessHours)}>
              {all.common.cancel}
            </button>
          ) : null}
          <StatusLine status={status} />
        </div>
      ) : null}
    </>
  );
}
